import React, { useState } from 'react';
import {
  Typography, TextField, Button,
  Checkbox, FormControlLabel, Alert, Box,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import useMessages from '../../../hooks/useMessages';
import FormInputMUI from '../../commun/FormInputMUI';
import FormLayout from '../../commun/FormLayout';
import Footer from '../../commun/Footer';
import './signup.css';

// Icônes
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhoneInput from 'react-phone-input-2';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const FormRecruteur = () => {
  const navigate = useNavigate();
  const { messages, handleSuccess, handleError } = useMessages();

  const [formData, setFormData] = useState({
    lastName: '', firstName: '', email: '', phone: '',
    linkedin: '', motDePasse: '', cguAccepted: false,
    verificationCode: '',
  });

  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(true);
  const [showVerification, setShowVerification] = useState(false);
  const [message, setMessage] = useState('');

  const setField = (name, value) =>
    setFormData(prev => ({ ...prev, [name]: value }));

  const validate = () => {
    const newErrors = {};
    if (!formData.lastName) newErrors.lastName = 'Champ requis.';
    if (!formData.firstName) newErrors.firstName = 'Champ requis.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Email invalide.';
    if (!/^\+?[0-9]{10,15}$/.test(formData.phone)) {
      newErrors.phone = 'Téléphone invalide.';
    }
    if (!formData.motDePasse || formData.motDePasse.length < 8)
      newErrors.motDePasse = 'Min. 8 caractères.';
    if (!formData.cguAccepted) newErrors.cguAccepted = 'CGU obligatoire.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return handleError('Veuillez remplir tous les champs requis.');

    try {
      await axios.post(`${API_BASE}/api/auth/signup-recruteur`, formData);
      handleSuccess('Inscription réussie. Code envoyé.');
      setShowForm(false);
      setShowVerification(true);
    } catch (err) {
      handleError(err.response?.data?.message || "Erreur lors de l'inscription.");
    }
  };

  const handleVerification = async e => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/api/auth/verify`, {
        email: formData.email,
        verificationCode: formData.verificationCode,
      });
      setMessage('Compte vérifié !');
      setTimeout(() => navigate('/login'), 2000);
    } catch {
      setMessage('Code de vérification incorrect.');
    }
  };

  return (
    <>
      <FormLayout
        infoTitle="Développez votre activité de recruteur indépendant"
        infoSubtitle="Postulez à des missions, proposez des talents, touchez vos primes"
        infoPoints={[
          "✓ Accédez à des missions publiées par des entreprises IT",
          "✓ Proposez vos talents en quelques clics",
          "✓ Gagnez des primes selon vos résultats"
        ]}
      >
        <Typography variant="h4" align="center" gutterBottom>
          Inscription Recruteur
        </Typography>

        {messages.error && <Alert severity="error">{messages.error}</Alert>}
        {messages.success && <Alert severity="success">{messages.success}</Alert>}

        {showForm && (
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
              gap={2}
            >
              <FormInputMUI
                label="Nom"
                required
                value={formData.lastName}
                onChange={val => setField('lastName', val)}
                error={errors.lastName}
                icon={<PersonIcon fontSize="small" />}
              />
              <FormInputMUI
                label="Prénom"
                required
                value={formData.firstName}
                onChange={val => setField('firstName', val)}
                error={errors.firstName}
                icon={<PersonIcon fontSize="small" />}
              />
              <FormInputMUI
                label="Email"
                type="email"
                required
                value={formData.email}
                onChange={val => setField('email', val)}
                error={errors.email}
                icon={<EmailIcon fontSize="small" />}
              />

              <Box>
                <PhoneInput
                  country="fr"
                  onlyCountries={['fr', 'ma', 'tn']}
                  value={formData.phone}
                  onChange={(value) => {
                    const fullNumber = `+${value}`;
                    setField('phone', fullNumber);
                  }}
                  inputStyle={{ width: '100%', height: 50 }}
                />

                {(errors.phone) && (
                  <p style={{ color: 'red' }}>{errors.phone}</p>
                )}
              </Box>

              <Box gridColumn="span 2">
                <FormInputMUI
                  label="Mot de passe"
                  type="password"
                  required
                  value={formData.motDePasse}
                  onChange={val => setField('motDePasse', val)}
                  error={errors.motDePasse}
                  icon={<LockIcon fontSize="small" />}
                />
              </Box>
              <Box gridColumn="span 2">
                <FormInputMUI
                  label="LinkedIn"
                  value={formData.linkedin}
                  onChange={val => setField('linkedin', val)}
                  icon={<LinkedInIcon fontSize="small" />}
                />
              </Box>
              <Box gridColumn="span 2">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.cguAccepted}
                      onChange={e => setField('cguAccepted', e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      J'accepte les&nbsp;
                      <a href="/cgu" target="_blank" rel="noopener noreferrer">CGU</a> *
                    </Typography>
                  }
                />
                {Object.values(errors).length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <div style={{ margin: 0, padding: 0 }}>
                      {Object.values(errors).map((err, idx) => (
                        <div key={idx}>{err}</div>
                      ))}
                    </div>
                  </Alert>
                )}

              </Box>
              <Box gridColumn="span 2">
                <Button type="submit" variant="contained" fullWidth>
                  Confirmer
                </Button>
              </Box>
            </Box>
          </form>
        )}

        {showVerification && (
          <form onSubmit={handleVerification} className="verif-wrap">
            <TextField
              label="Code de vérification"
              fullWidth
              required
              value={formData.verificationCode}
              onChange={e => setField('verificationCode', e.target.value)}
              margin="normal"
            />
            <Button variant="outlined" type="submit" fullWidth>
              Vérifier le compte
            </Button>
          </form>
        )}

        {message && <Typography className="fr-message">{message}</Typography>}
      </FormLayout>
      <Footer />
    </>
  );
};

export default FormRecruteur;
